import { clsx } from 'clsx';
import currencyFormatting from '@/utils/currencyFormatting';
import useCalcItemPrice from '@/hooks/useCalcItemPrice';
import styles from './SearchResultsPrice.module.css';
import { SearchResultsPriceProps } from './types';

const SearchResultsPrice = ({
  priceCents,
  pricePerUnit,
  pricePerUnitLabel,
  ProductConditionMax,
  ProductCondition,
  salePriceCents,
  NewPriceMax,
  NewPriceMin,
  NewSalePriceMax,
  NewSalePriceMin,
  UsedAnyPriceMin,
  priceContainerClassName,
  discountCents,
  quantity,
}: SearchResultsPriceProps) => {
  const { getSalesPrice, getOriginalPrice, getProductConditionalPrice, isRefurbished } = useCalcItemPrice({
    newPriceMin: NewPriceMin || null,
    newPriceMax: NewPriceMax || null,
    newSalePriceMin: NewSalePriceMin || null,
    newSalePriceMax: NewSalePriceMax || null,
    productConditionMax: ProductConditionMax || null,
    productCondition: ProductCondition || null,
    priceCents,
    salePriceCents,
    UsedAnyPriceMin,
  });

  const renderWithPricePerUnit = () =>
    pricePerUnit && (
      <span
        className={`${styles.rdLabel} ${styles.pricePerUnit} pricePerUnit`}
      >{`${pricePerUnit} ${pricePerUnitLabel!}`}</span>
    );

  const renderItemsAmount = () =>
    quantity && quantity > 1 ? <span className={`${styles.priceItemsAmount}`}>{`x${quantity}`}</span> : null;

  const renderSalesPrice = (
    salesPrice: string | null | undefined,
    productConditionPrice: string | null | undefined,
  ) => {
    if (productConditionPrice === 'FREE') {
      return <p className={`${styles.searchItemPrice} salesPrice`}>{`FREE`}</p>;
    }

    return salePriceCents ? (
      <p className={`${styles.searchItemPrice} salesPrice`}>
        {salesPrice}
        {renderWithPricePerUnit()}
      </p>
    ) : null;
  };

  const renderOriginalPrice = (
    originalPrice: string | null | undefined,
    salesPrice: string | null | undefined,
    productConditionPrice: string | null | undefined,
  ) => {
    if (productConditionPrice === 'FREE' || salesPrice === originalPrice) {
      return null;
    }
    if (isRefurbished && productConditionPrice) {
      return (
        <span className={`${styles.rdLabel} ${styles.originalPrice} ${styles.refurbPrice} originalPrice`}>
          {`${ProductConditionMax!}.`}
          {UsedAnyPriceMin && ` ${productConditionPrice}`}
        </span>
      );
    } else {
      return (
        <>
          {originalPrice ? (
            <span className={`${styles.rdLabel} ${styles.originalPrice} ${styles.refurbPrice} originalPrice`}>
              <span>{originalPrice}</span>
            </span>
          ) : null}
        </>
      );
    }
  };

  const renderDiscountAmount = (discountCents: number) => (
    <p className={`${styles.discountAmount}`}>
      {`${currencyFormatting(discountCents / 100)} off`}
      <span className={styles.asterisk}>*</span>
    </p>
  );

  const renderSearchResultsPrice = () => {
    const salesPrice = getSalesPrice();
    const originalPrice = getOriginalPrice();
    const productConditionPrice = getProductConditionalPrice();
    return (
      <div className={clsx(priceContainerClassName)}>
        {renderSalesPrice(salesPrice, productConditionPrice)}
        {renderOriginalPrice(originalPrice, salesPrice, productConditionPrice)}
        {discountCents && discountCents > 0 && productConditionPrice !== 'FREE'
          ? renderDiscountAmount(discountCents)
          : null}
      </div>
    );
  };

  return <>{renderSearchResultsPrice()}</>;
};

export default SearchResultsPrice;
